import classNames from 'classnames';
import { Link, useNavigate } from 'react-router-dom';

import { DemoPill, StateMessage } from '@/components/ui/controls';
import { PageHeader } from '@/components/ui/page';
import { SortTh } from '@/components/ui/sort-th';
import { btn, table } from '@/components/ui/styles';
import { formatDate } from '@/utils/format';
import { useApi } from '@/utils/use-api';
import { dateValue, useSort, type SortColumns } from '@/utils/use-sort';

import type { Template } from './api-types';
import MessagingNav from './messaging-nav';
import { ChannelList } from './parts';

const COLUMNS: SortColumns<Template> = {
  name: { value: (t) => t.name },
  used: { value: (t) => t.used_by, desc: true },
  updated: { value: (t) => dateValue(t.updated_at), desc: true },
};

/** Reusable texts for messages and triggers (OUT-1, OUT-2). */
const TemplatesPage = () => {
  const navigate = useNavigate();
  const { data, error } = useApi<Template[]>('/templates');
  const open = (id: string) => navigate(`/messaging/templates/${id}`);
  const { rows, sort, toggle } = useSort(data ?? [], COLUMNS);

  return (
    <div>
      <PageHeader
        title="Templates"
        description="Texts the team and the triggers reuse, with variables such as the owner's first name."
        actions={
          <Link to="/messaging/templates/new" className={btn.primary}>
            New template
          </Link>
        }
      >
        <MessagingNav />
      </PageHeader>

      {error && !data ? (
        <StateMessage title="Templates could not be loaded" tone="error">
          {error}
        </StateMessage>
      ) : data && data.length === 0 ? (
        <StateMessage title="No templates yet">
          <Link to="/messaging/templates/new" className={btn.link}>
            Write one
          </Link>
        </StateMessage>
      ) : (
        <div className={table.wrap}>
          <div className="overflow-x-auto">
            <table className={classNames(table.table, 'md:min-w-[760px]')}>
              <thead className={classNames(table.head, 'hidden md:table-header-group')}>
                <tr>
                  <SortTh label="Template" column="name" sort={sort} onSort={toggle} />
                  <th scope="col" className={table.th}>Channels</th>
                  <SortTh label="Used by" column="used" sort={sort} onSort={toggle} right />
                  <SortTh label="Updated" column="updated" sort={sort} onSort={toggle} />
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => (
                  <tr
                    key={t.id}
                    tabIndex={0}
                    onClick={() => open(t.id)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') open(t.id);
                    }}
                    className={classNames(table.rowLink, 'focus:outline-none focus-visible:bg-surface-hover')}
                  >
                    <td className={table.td}>
                      <p className="flex flex-wrap items-center gap-2 break-words text-fg-strong">
                        {t.name}
                        {t.demo && <DemoPill title="From the local demo seed">Demo</DemoPill>}
                      </p>
                      <p className="break-words text-xs text-fg-muted">{t.subject || '—'}</p>
                      {/* On a phone the other columns fold in here. */}
                      <p className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-fg-muted md:hidden">
                        <ChannelList channels={t.channels} iconsOnly />
                        Used by {t.used_by} · {formatDate(t.updated_at)}
                      </p>
                    </td>
                    <td className={classNames(table.td, 'hidden md:table-cell')}>
                      <ChannelList channels={t.channels} iconsOnly />
                    </td>
                    <td className={classNames(table.td, 'hidden text-right tabular-nums md:table-cell')}>{t.used_by}</td>
                    <td className={classNames(table.td, 'hidden whitespace-nowrap text-fg-muted md:table-cell')}>
                      {formatDate(t.updated_at)}
                      {t.updated_by && <span className="block text-xs text-fg-faint">by {t.updated_by}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplatesPage;
